import { Directive, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { RtFormLabelsFromForm, RtFormPlaceholdersFromForm, RtValidationMessagesFromForm } from 'rt-inputs';
import { Subscription } from 'rxjs';
import { AUTH_METHOD } from 'symbols';
import { Login, LoginFail, LoginSuccess } from '../states/gp-auth/gp-auth.actions';
import { GpAuthState } from '../states/gp-auth/gp-auth.state';
import { GpAuthConfigModel, LoginData } from '../symbols';

@Directive({
  standalone: true
})
export abstract class LoginDirective implements OnInit, OnDestroy {

  public readonly isAuthenticated$ = this.store.select(GpAuthState.isAuthenticated);
  public readonly authStatus$ = this.store.select(GpAuthState.loginStatus);


  public readonly subscription = new Subscription();
  public readonly authMethod = AUTH_METHOD;

  public form = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    tel: new FormControl(null, [Validators.required, Validators.pattern(/^0\d{9}$/)]),
    email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(200)]),
    password: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    remember_me: new FormControl(false)
  });

  public labels: RtFormLabelsFromForm<typeof this.form> = {
    username: $localize`User name`,
    email: $localize`Email`,
    tel: $localize`Phone number`,
    password: $localize`Password`,
    remember_me: $localize`Remember me`
  };

  public validationMessages: RtValidationMessagesFromForm<typeof this.form> = {
    username: {
      required: $localize`User name is required`,
      maxlength: $localize`User name should not be more than 200 characters`
    },
    email: {
      required: $localize`Email is required`,
      email: $localize`Email is not valid`,
      maxlength: $localize`Email should not be more than 200 characters`
    },
    tel: {
      required: $localize`Phone number is required`,
      pattern: $localize`Phone number is not valid`
    },
    password: {
      required: $localize`Password is required`,
      maxlength: $localize`Password should not be more than 200 characters`
    },
    remember_me: {}
  };

  public placeholders: RtFormPlaceholdersFromForm<typeof this.form> = {
    username: $localize `Enter user name`,
    email: $localize `Enter email`,
    password: $localize `Enter password`,
    tel: $localize `Enter phone number`,
    remember_me: ''
  };

  protected constructor(protected store: Store, protected actions$: Actions, protected authConfig: GpAuthConfigModel) {
  }

  ngOnInit() {
    this.defineFormControls();
    this.subscription.add(
      this.actions$.pipe(ofActionSuccessful(LoginSuccess, LoginFail))
        .subscribe({
          next: () => {
            this.form.enable();
            this.defineFormControls();
          }
        })
    );
  }

  onSubmit() {
    if (this.form.valid) {
      this.store.dispatch(new Login(this.form.value as LoginData));
      this.form.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private defineFormControls() {
    switch (this.authConfig.authMethod) {
      case AUTH_METHOD.EMAIL:
        this.form.controls.username.disable();
        this.form.controls.tel.disable();
        break;
      case AUTH_METHOD.USERNAME:
        this.form.controls.email.disable();
        this.form.controls.tel.disable();
        break;
      case AUTH_METHOD.TELEGRAM_BOT:
      case AUTH_METHOD.PHONE:
        this.form.controls.email.disable();
        this.form.controls.username.disable();
        break;
    }
  }
}
